import type { CsvIntegrationConfig } from '@entities/platform-entities/integration/csv-integration-config.interface';
import type { CsvTerrificField } from '@entities/platform-entities/integration/csv-terrific-field.type';
import type { CsvVariantGroupingStrategy } from '@entities/platform-entities/integration/csv-variant-grouping-strategy.type';

const GOOGLE_FIELD_ALIASES: Partial<Record<CsvTerrificField, string[]>> = {
  description: ['description'],
  externalId: ['id'],
  externalUrl: ['link'],
  imageUrl: ['image_link'],
  price: ['price'],
  salePrice: ['sale_price'],
  title: ['title'],
};

const META_FIELD_ALIASES: Partial<Record<CsvTerrificField, string[]>> = {
  ...GOOGLE_FIELD_ALIASES,
  color: ['color'],
};

function findHeader(headers: string[], aliases: string[]): string | undefined {
  const normalized = new Map(headers.map((header) => [header.toLowerCase(), header]));
  return aliases.map((alias) => normalized.get(alias.toLowerCase())).find(Boolean);
}

function detectFeedPreset(headers: string[]): 'google' | 'meta' | null {
  const lower = new Set(headers.map((header) => header.toLowerCase()));
  if (lower.has('image_link') && lower.has('link')) {
    return lower.has('color') ? 'meta' : 'google';
  }
  return null;
}

export function applyCsvFeedPresets(
  headers: string[],
  existing?: CsvIntegrationConfig
): CsvIntegrationConfig {
  const preset = detectFeedPreset(headers);
  const aliases = preset === 'meta' ? META_FIELD_ALIASES : GOOGLE_FIELD_ALIASES;

  const fieldMapping: CsvIntegrationConfig['fieldMapping'] = {
    ...existing?.fieldMapping,
  };

  for (const [field, columnAliases] of Object.entries(aliases) as [CsvTerrificField, string[]][]) {
    if (fieldMapping[field]) {
      continue;
    }
    const match = findHeader(headers, columnAliases);
    if (match) {
      fieldMapping[field] = match;
    }
  }

  const variantGroupingStrategy: CsvVariantGroupingStrategy =
    existing?.variantGrouping?.strategy ?? 'title';

  return {
    delimiter: existing?.delimiter ?? 'auto',
    fieldMapping,
    variantGrouping: {
      customField: existing?.variantGrouping?.customField,
      strategy: variantGroupingStrategy,
    },
  };
}
